import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface BranchContextType {
  selectedBranch: number | null;
  setSelectedBranch: (branchId: number | null) => void;
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

const SELECTED_BRANCH_STORAGE_KEY = 'selectedBranchId';

interface BranchProviderProps {
  children: ReactNode;
}

export function BranchProvider({ children }: BranchProviderProps) {
  // Load selectedBranch from localStorage on mount
  const [selectedBranch, setSelectedBranchState] = useState<number | null>(() => {
    const stored = localStorage.getItem(SELECTED_BRANCH_STORAGE_KEY);
    return stored ? parseInt(stored, 10) : null;
  });

  const { user, userBranches, isLoading: authLoading } = useAuth();

  // The persisted branch is only valid for the user it was chosen by. Once auth state is known,
  // drop it on logout, and on login/refresh replace it with the user's default branch unless it
  // is one of the user's branches.
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setSelectedBranchState(null);
      return;
    }

    if (userBranches.length === 0) return;

    const isAllowed =
      selectedBranch !== null && userBranches.some((b) => b.branchId === selectedBranch);
    if (!isAllowed) {
      setSelectedBranchState(user.defaultBranchId ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, userBranches]);

  // Persist selectedBranch to localStorage whenever it changes
  useEffect(() => {
    if (selectedBranch !== null) {
      localStorage.setItem(SELECTED_BRANCH_STORAGE_KEY, selectedBranch.toString());
    } else {
      localStorage.removeItem(SELECTED_BRANCH_STORAGE_KEY);
    }
  }, [selectedBranch]);

  const setSelectedBranch = (branchId: number | null) => {
    setSelectedBranchState(branchId);
  };

  return (
    <BranchContext.Provider value={{ selectedBranch, setSelectedBranch }}>
      {children}
    </BranchContext.Provider>
  );
}

export function useBranch() {
  const context = useContext(BranchContext);
  if (context === undefined) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
}
